import React from 'react';

const WhyUs = () => {
  const pharmaPartners = [
    { name: 'SCHOTT Poonawalla', icon: 'fas fa-vial' },
    { name: 'Borosil Scientific', icon: 'fas fa-prescription-bottle-alt' },
    { name: 'Nipro', icon: 'fas fa-capsules' },
    { name: 'Gerresheimer', icon: 'fas fa-flask' },
    { name: 'Khemka Group', icon: 'fas fa-industry' },
    { name: 'Glassco Group', icon: 'fas fa-microscope' }
  ];

  return (
    <section id="why-us" className="why-us-section py-5 bg-white border-bottom position-relative">
      <div className="container py-lg-4">
        {/* Section Header */}
        <div className="text-center max-w-700 mx-auto mb-5">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-light border text-primary small fw-semibold mb-2 shadow-2xs">
            <i className="fas fa-shield-alt"></i>
            <span>Proven Reliability Since 1997</span>
          </div>
          <h2 className="fw-bold text-dark mb-2 display-6">
            Why Choose <b>A.B. Engineering Works</b>
          </h2>
          <div className="text-primary fw-semibold fs-5 mb-2">
            Trusted Partnerships &amp; Uncompromising Engineering Standards
          </div>
          <p className="text-muted fs-6 mb-0" style={{ lineHeight: '1.6' }}>
            We combine nearly three decades of hands-on precision machining expertise with delivering machinery spares that keep high-speed pharmaceutical packaging lines operating with zero unplanned downtime.
          </p>
        </div>

        {/* Main Content: 5 Trust Pillars + Client Endorsement Side Card */}
        <div className="row g-4 align-items-stretch mb-5">
          {/* Left Column: Trust Pillars (col-lg-8) */}
          <div className="col-lg-8">
            <div className="row g-3">
              {/* Card 1: 28+ Years Industry Mastery */}
              <div className="col-md-6">
                <div className="trust-card p-4 rounded-3 border bg-light h-100 d-flex flex-column justify-content-between shadow-2xs">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="trust-icon-box bg-white border rounded-circle d-flex align-items-center justify-content-center text-primary" style={{ width: '48px', height: '48px' }}>
                        <i className="fas fa-award fs-5"></i>
                      </div>
                      <span className="badge bg-dark bg-opacity-75 text-white fw-normal px-2 py-1">
                        Est. 1997
                      </span>
                    </div>
                    <h5 className="fw-bold text-dark mb-2 fs-6">
                      28+ Years Industry Mastery
                    </h5>
                    <p className="text-secondary small mb-3" style={{ lineHeight: '1.5' }}>
                      Over 28 years dedicated exclusively to pharmaceutical packaging machinery, ampoule forming &amp; sealing lines.
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2 text-xs text-muted border-top pt-2">
                    <i className="fas fa-check-circle text-success"></i>
                    <span>Over 10,000+ precision parts fabricated</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Trusted by 25+ Pharma Giants */}
              <div className="col-md-6">
                <div className="trust-card p-4 rounded-3 border bg-light h-100 d-flex flex-column justify-content-between shadow-2xs">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="trust-icon-box bg-white border rounded-circle d-flex align-items-center justify-content-center text-primary" style={{ width: '48px', height: '48px' }}>
                        <i className="fas fa-handshake fs-5"></i>
                      </div>
                      <span className="badge bg-primary text-white fw-semibold px-2 py-1">
                        25+ Clients
                      </span>
                    </div>
                    <h5 className="fw-bold text-dark mb-2 fs-6">
                      Trusted by 25+ Leading Pharma Giants
                    </h5>
                    <p className="text-secondary small mb-3" style={{ lineHeight: '1.5' }}>
                      Preferred OEM replacement spares vendor for leading pharmaceutical manufacturing facilities and contract packaging plants across Maharashtra &amp; India.
                    </p>
                  </div>
                  {/* Mini Partner Chips - All 6 Partners */}
                  <div className="d-flex flex-wrap gap-1 border-top pt-2">
                    {pharmaPartners.map((p, idx) => (
                      <span key={idx} className="badge bg-white text-dark border px-2 py-1 small fw-normal">
                        <i className={`${p.icon} text-primary me-1 text-xs`}></i>{p.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card 3: Zero-Defect Quality Guarantee */}
              <div className="col-md-6">
                <div className="trust-card p-4 rounded-3 border bg-light h-100 d-flex flex-column justify-content-between shadow-2xs">
                  <div>
                    <div className="trust-icon-box bg-white border rounded-circle d-flex align-items-center justify-content-center text-success mb-3" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-microscope fs-5"></i>
                    </div>
                    <h5 className="fw-bold text-dark mb-2 fs-6">
                      Zero-Defect Quality Guarantee
                    </h5>
                    <p className="text-secondary small mb-3" style={{ lineHeight: '1.5' }}>
                      100% optical micrometer and dial gauge verification to ensure micro-tolerances down to ±0.005mm. Every component fits seamlessly with OEM specifications.
                    </p>
                  </div>
                  <div className="text-xs fw-semibold text-success border-top pt-2 d-flex align-items-center gap-1">
                    <i className="fas fa-shield-alt"></i>
                    <span>100% Fitment Guarantee</span>
                  </div>
                </div>
              </div>

              {/* Card 4: Rapid Breakdown & Repair Support */}
              <div className="col-md-6">
                <div className="trust-card p-4 rounded-3 border bg-light h-100 d-flex flex-column justify-content-between shadow-2xs">
                  <div>
                    <div className="trust-icon-box bg-white border rounded-circle d-flex align-items-center justify-content-center text-danger mb-3" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-bolt fs-5"></i>
                    </div>
                    <h5 className="fw-bold text-dark mb-2 fs-6">
                      Rapid Breakdown Support
                    </h5>
                    <p className="text-secondary small mb-3" style={{ lineHeight: '1.5' }}>
                      Emergency fabrication turnaround from physical sample parts or sketches to prevent costly packaging line downtime with fast dispatch.
                    </p>
                  </div>
                  <div className="text-xs fw-semibold text-danger border-top pt-2 d-flex align-items-center gap-1">
                    <i className="fas fa-clock"></i>
                    <span>Same-Day Response</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: MSME Government Accreditation & Vendor Verification (col-lg-4) */}
          <div className="col-lg-4">
            <div
              className="msme-showcase-card p-4 rounded-3 border bg-white h-100 d-flex flex-column justify-content-between shadow-xs position-relative overflow-hidden"
              style={{ borderTop: '4px solid #d4af37' }}
            >
              <div>
                {/* Header Tag */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="badge bg-warning-subtle text-dark border border-warning border-opacity-50 px-2.5 py-1 small fw-semibold">
                    <i className="fas fa-certificate text-warning me-1"></i> Govt. Recognized
                  </span>
                  <span className="text-muted text-xs fw-semibold">Udyam Registration</span>
                </div>

                {/* Centerpiece: Golden MSME Logo Showcase */}
                <div className="text-center p-3 rounded-3 bg-light border border-warning border-opacity-25 mb-3 position-relative">
                  <img
                    src="/msme-gold-logo.png"
                    alt="Ministry of MSME Govt of India"
                    className="img-fluid object-fit-contain"
                    style={{ maxHeight: '82px' }}
                  />
                  <div className="mt-2 fw-bold text-dark fs-6 lh-sm">
                    Registered MSME Enterprise
                  </div>
                  <div className="text-muted text-xs mt-0.5">
                    Ministry of Micro, Small &amp; Medium Enterprises
                  </div>
                </div>

                {/* Statutory & Procurement Advantages */}
                <div className="mb-3">
                  <div className="text-xs fw-bold text-uppercase text-muted mb-2 tracking-wide">
                    Corporate Procurement Highlights
                  </div>
                  <ul className="list-unstyled d-flex flex-column gap-2 small text-secondary mb-0">
                    <li className="d-flex align-items-start gap-2">
                      <i className="fas fa-check-circle text-success mt-1"></i>
                      <span><strong>Pre-Qualified Vendor Status:</strong> Verified credentials for corporate pharmaceutical vendor onboarding.</span>
                    </li>
                    <li className="d-flex align-items-start gap-2">
                      <i className="fas fa-check-circle text-success mt-1"></i>
                      <span><strong>Make in India Tooling:</strong> High-precision domestic replacement spares reducing import lead time by 80%.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Bottom Card Action */}
              <div className="border-top pt-3 mt-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="fw-bold text-dark small lh-1">Vendor Code Ready</div>
                    <span className="text-muted text-xs">GST &amp; MSME Verified</span>
                  </div>
                  <a
                    href="#contact"
                    className="btn btn-sm btn-primary px-3 py-1.5 fw-semibold rounded-2 d-inline-flex align-items-center gap-1.5 shadow-2xs"
                  >
                    <span>Request Details</span>
                    <i className="fas fa-arrow-right text-xs"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Callout Banner: Elevate Your Production Line */}
        <div className="cta-banner rounded-3 p-4 p-lg-5 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <div className="d-flex align-items-start gap-3">
                <div className="bg-primary text-white rounded-circle d-none d-sm-flex align-items-center justify-content-center p-3 flex-shrink-0" style={{ width: '56px', height: '56px' }}>
                  <i className="fas fa-cogs fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold mb-2 fs-4 text-white">
                    Elevate Your Production Line Efficiency with A.B. Engineering
                  </h3>
                  <p className="text-white-50 mb-0 small" style={{ lineHeight: '1.6' }}>
                    Connect directly with our tooling engineers today. Send your CAD drawings or component samples for a rapid.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 text-lg-end text-start">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-lg-end">
                <a
                  href="#contact"
                  className="btn btn-primary px-3 px-sm-4 py-2 fw-semibold rounded-2 shadow-xs d-inline-flex align-items-center justify-content-center gap-2 banner-btn"
                >
                  <i className="fas fa-file-invoice"></i>
                  <span>Get a Free Quote</span>
                </a>
                <a
                  href="https://wa.me/918108765006?text=Hello%20A.B.%20Engineering%20Works,%20I%20would%20like%20to%20consult%20regarding%20machinery%20spares%20and%20tooling."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-light px-3 py-2 fw-semibold rounded-2 d-inline-flex align-items-center justify-content-center gap-2 banner-btn"
                >
                  <i className="fab fa-whatsapp text-success fs-5"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
